import { useEffect, useMemo, useRef, useState } from "react";
import { FormInputIcon } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Attachments } from "./Attachments";
import { Business } from "./Business";
import { Culture } from "./Culture";
import { GeneralInfo } from "./GeneralInfo";
import { SalesTeam } from "./SalesTeam";
import { Sites } from "./Sites";
import { Stakeholders } from "./Stakeholders";
import { TalentRecruitingHistory } from "./TalentRecruitingHistory";
import { AutosaveFormNumberSelect } from "@/components/companies/shared/autosave-form/AutosaveFormNumberSelect";
import { IntakeCompletionDialog } from "@/components/companies/shared/IntakeCompletionDialog";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { IntakeHeader } from "@/components/companies/shared/IntakeHeader";
import { IntakeProgress } from "@/components/companies/shared/IntakeProgress";
import { TableOfContentsButton } from "@/components/companies/shared/TableOfContentsButton";
import {
  Center,
  Column,
  EmptyState,
  ErrorText,
  LoadingIndicator,
  Row,
} from "@/components";

import { useUserOptionsWithSelected } from "@/contexts/TaggableUsersContext";
import { useGetCompanyIntake } from "@/hooks/queries/useGetCompanyIntake";
import { useGetStakeholders } from "@/hooks/queries/useGetStakeholders";
import { useUpdateSetCompanyIntake } from "@/hooks/updateSets/useUpdateSetCompanyIntake";
import { intake } from "@/lib/intakeProgress";
import { companyIntakeTabToLabel } from "@/lib/constants";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { nullableNumberSchema } from "@/lib/schemas";
import { cn } from "@/lib/utils";
import { CompanyIntakeTab } from "@/types";

export const CompanyIntakeContainer = ({
  className,
  companyId,
  companyName,
}: {
  className?: string;
  companyId: string;
  companyName: string;
}) => {
  /* Database CRUD Operations */
  // Read
  const {
    data: companyIntake,
    isPending: isCompanyIntakePending,
    isError: isCompanyIntakeError,
  } = useGetCompanyIntake();
  const { data: stakeholders, isPending: isStakeholdersPending } =
    useGetStakeholders();

  // Update
  const { mutate: updateCompanyIntake } = useUpdateSetCompanyIntake();

  /* Misc Hooks */
  const userOptions = useUserOptionsWithSelected([
    companyIntake?.ownerId ?? null,
  ]);

  /* React Hooks */
  // useState
  const [activeTab, setActiveTab] = useState<CompanyIntakeTab>(
    CompanyIntakeTab.SalesTeam,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isCompletionDialogOpen, setIsCompletionDialogOpen] = useState(false);

  // useRef
  const scrollRef = useRef<HTMLDivElement>(null);

  // useMemo
  const tabProgress = useMemo(() => {
    if (!companyIntake) return null;
    return intake.getCompanyTabProgress(companyIntake, stakeholders ?? []);
  }, [companyIntake, stakeholders]);

  // useEffect
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeTab]);

  /* Handlers */
  const handleIntakeComplete = () => {
    setIsCompletionDialogOpen(true);
  };

  const render = () => {
    if (isCompanyIntakePending) {
      return <LoadingIndicator variant="medium" />;
    }

    if (isCompanyIntakeError) {
      return (
        <Center>
          <ErrorText errorMessage="An error occurred while loading the company intake. Please let the engineers know if they are not already on it!" />
        </Center>
      );
    }

    if (!companyIntake) {
      // Intake is now seeded on the backend when a company is created, so this
      // should only show briefly for a brand-new company before the row syncs.
      return (
        <EmptyState
          description="This company's intake is being set up. Refresh in a moment — let the engineers know if it doesn't appear."
          Icon={FormInputIcon}
          title="Company Intake"
        />
      );
    }

    return (
      <Row className={cn("h-[calc(100vh-200px)]", className)}>
        <Column className="border-r border-slate-200 px-6 pb-0">
          <OwnerSelect
            companyIntake={companyIntake}
            userOptions={userOptions}
            updateCompanyIntake={updateCompanyIntake}
          />
          <p className="mb-4 text-sm uppercase tracking-wider text-slate-600">
            Steps
          </p>
          <Column className="gap-1">
            {Object.values(CompanyIntakeTab).map((tab, index) => (
              <TableOfContentsButton
                key={tab}
                activeTab={activeTab}
                index={index + 1}
                isDataSettled={!isStakeholdersPending}
                label={companyIntakeTabToLabel[tab]}
                screenName={MIXPANEL_SCREENS.COMPANY}
                progress={
                  tabProgress?.[tab]
                    ? {
                        ...tabProgress[tab],
                        graceNote:
                          tab === CompanyIntakeTab.GeneralInfo
                            ? "Media links are optional and do not count toward progress"
                            : undefined,
                      }
                    : { filled: 0, total: 0 }
                }
                setActiveTab={setActiveTab}
                tabValue={tab}
              />
            ))}
          </Column>
          <IntakeProgress
            {...intake.getCompanyProgress(companyIntake, stakeholders ?? [])}
            isDataSettled={!isStakeholdersPending}
            onComplete={handleIntakeComplete}
          />
          <div className="mt-auto">
            <Attachments companyIntake={companyIntake} />
          </div>
        </Column>
        <div
          ref={scrollRef}
          className="flex flex-1 flex-col overflow-y-auto px-12 py-0"
        >
          <IntakeHeader
            activeTab={activeTab}
            isEditing={isEditing}
            screenName={MIXPANEL_SCREENS.COMPANY}
            setActiveTab={setActiveTab}
            setIsEditing={setIsEditing}
            tabs={Object.values(CompanyIntakeTab)}
            tabToLabel={companyIntakeTabToLabel}
          />
          <div className="max-w-3xl">
            {activeTab === CompanyIntakeTab.SalesTeam && (
              <SalesTeam companyIntake={companyIntake} isEditing={isEditing} />
            )}
            {activeTab === CompanyIntakeTab.Stakeholders && (
              <Stakeholders isEditing={isEditing} setIsEditing={setIsEditing} />
            )}
            {activeTab === CompanyIntakeTab.GeneralInfo && (
              <GeneralInfo
                companyIntake={companyIntake}
                isEditing={isEditing}
              />
            )}
            {activeTab === CompanyIntakeTab.Sites && (
              <Sites
                companyIntake={companyIntake}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            )}
            {activeTab === CompanyIntakeTab.TalentRecruitingHistory && (
              <TalentRecruitingHistory
                companyIntake={companyIntake}
                isEditing={isEditing}
              />
            )}
            {activeTab === CompanyIntakeTab.Culture && (
              <Culture companyIntake={companyIntake} isEditing={isEditing} />
            )}
            {activeTab === CompanyIntakeTab.Business && (
              <Business companyIntake={companyIntake} isEditing={isEditing} />
            )}
          </div>
        </div>
        <IntakeCompletionDialog
          entityName={companyName}
          onOpenChange={setIsCompletionDialogOpen}
          open={isCompletionDialogOpen}
          shareUrl={`${window.location.origin}/companies/${companyId}?t=intake`}
          type="company"
        />
      </Row>
    );
  };

  return render();
};

const ownerSchema = z.object({
  owner: nullableNumberSchema,
});

const OwnerSelect = ({
  companyIntake,
  userOptions,
  updateCompanyIntake,
}: {
  companyIntake: { ownerId: number | null };
  userOptions: { value: number; label: string }[];
  updateCompanyIntake: (args: {
    updateCompanyIntakeRequest: { ownerId: number | null };
  }) => void;
}) => {
  /* React Hooks */
  const form = useForm<z.infer<typeof ownerSchema>>({
    resolver: zodResolver(ownerSchema),
    defaultValues: { owner: companyIntake.ownerId },
  });

  /* Handlers */
  const handleSave = (data: z.infer<typeof ownerSchema>) => {
    updateCompanyIntake({
      updateCompanyIntakeRequest: { ownerId: data.owner ?? null },
    });
  };

  const saveOnSelect = () => {
    void form.handleSubmit(handleSave)();
  };

  return (
    <Column className="mb-4">
      <IntakeForm form={form} onSave={saveOnSelect}>
        <AutosaveFormNumberSelect
          fieldName="owner"
          form={form}
          label="Company Intake Owner"
          onSelect={saveOnSelect}
          options={userOptions}
        />
      </IntakeForm>
    </Column>
  );
};
